import { term, type TermId } from '../glossary'
import { Popover } from './ui/Popover'

/**
 * A domain word, wearing its own definition.
 *
 * **The inline gloss is the feature; the panel is the fallback.** Chen (2016) compared gloss modes
 * head to head and pop-ups scored lowest on every test — the mechanism being split attention, since
 * a reader has to leave the sentence to get the meaning and then find their place again. NN/g's
 * jargon guidance lands in the same place and calls the winner *paired terminology*: write the
 * meaning beside the word, inline, where it is used. So `define` renders the definition into the
 * sentence, and the tappable panel exists only for a reader who wants more than the fragment.
 *
 * Tap-triggered, not hover. A hover tooltip is not a definition mechanism on a phone; it is nothing
 * at all, and this is a site people will check on a phone minutes before a match starts.
 *
 * `define` is a prop rather than an automatic first-occurrence rule. Claiming "first" during render
 * means mutating shared state while rendering, which double-invokes under StrictMode and has no
 * defined order under concurrent rendering — the failure being a gloss that renders once and then
 * vanishes. Authoring it is also better teaching: the right occurrence is the one in a sentence with
 * room for it, not whichever the reconciler reaches first.
 *
 * The panel's placement and dismissal live in {@link Popover}, which was extracted from this file
 * when the site's numbers needed the same behaviour. See {@link Detail} for that side.
 *
 * @example
 * <Term id="stake" />              // "Stake", tappable
 * <Term id="stake" define />       // "Stake (how much of your bankroll to risk)"
 * <Term id="stake">staking</Term>  // your own inflection
 */
export function Term({ id, define = false, children, className = '' }: {
  id: TermId
  /** Render the parenthetical inline. Use on the occurrence that should teach the word. */
  define?: boolean
  /** Override the rendered word, for casing or inflection. */
  children?: React.ReactNode
  className?: string
}) {
  const entry = term(id)

  return (
    <>
      <Popover
        label={`${entry.term} — what does this mean?`}
        trigger={children ?? entry.term}
        // Accent, and solid on hover: a word the reader may not know is worth interrupting a
        // sentence for. `Detail` uses a quieter neutral rule for the same reason in reverse.
        triggerClassName={`border-b border-dotted border-accent hover:border-solid ${className}`}
      >
        <span className="block text-sm font-medium text-ink">{entry.term}</span>
        <span className="mt-1 block text-xs leading-relaxed text-ink-soft">{entry.definition}</span>
        {entry.why ? (
          <span className="mt-2 block border-t border-line pt-2 text-xs leading-relaxed text-ink-faint">
            {entry.why}
          </span>
        ) : null}
      </Popover>

      {/* The gloss, in the sentence. This is the part that does the teaching. */}
      {define ? <span className="text-ink-faint"> ({entry.gloss})</span> : null}
    </>
  )
}
